import { usePageStore } from '@voix/store/pageStore'

// Type definition for utag
declare global {
  interface Window {
    utag?: {
      view: (data: Record<string, any>) => void
    } | undefined
  }
}

// Tealium data interface
interface TealiumData {
  language: string
  page_name: string
  page_type: string
  hotel_brand?: string
  hotel_name?: string
  hotel_property_code?: string
}

export default async function useTealium() {
  const pageStore = usePageStore()
  const page = ref(pageStore.currentPage)
  const { getCommonLayoutData } = usePlayaLayout()
  const { currentResort } = await getCommonLayoutData(false)

  // Promise function to wait for utag to become available
  // utag is a global variable that is set by the utag.js script in the head of the page
  async function waitForUtag(): Promise<void> {
    return new Promise((resolve, reject) => {
      let attempts = 0
      const maxAttempts = 5

      const checkUtag = () => {
        attempts++
        if (typeof window !== 'undefined' && window.utag !== undefined) {
          resolve()
        }
        else if (attempts >= maxAttempts) {
          reject(new Error('[Tealium] utag not available'))
        }
        else {
          setTimeout(checkUtag, 1000)
        }
      }
      checkUtag()
    })
  }

  // Call Tealium tracking when utag is available
  async function tealiumView() {
    await waitForUtag().then(() => {
      // Count forward slashes in the page path to determine if the page is a hotel property page
      const isHotelPropertyPage = () => {
        return (page.value.path || '').split('/').length - 1 === 2
      }

      // Set Content Page Data
      const data: TealiumData = {
        language: (page.value as { language_code: string })?.language_code,
        page_name: `${(page.value as { layout: string, title: string })?.layout} : ${(page.value as { layout: string, title: string })?.title}`,
        page_type: `content page`,
      }

      // Set Home Page Data
      if (page.value.path === '/') {
        data.page_type = 'home page'
      }

      // Set Hotel Property Page Data
      if (page.value.path === `/${currentResort.value.data.url_partial}` && isHotelPropertyPage()) {
        data.page_type = 'hotel property page'
        data.hotel_brand = (page.value as { layout: string })?.layout
        data.hotel_name = (currentResort as { value: { data: { name: string } } })?.value?.data?.name
        data.hotel_property_code = (currentResort as { value: { data: { booking_settings: { synxis_hotel_num: string } } } })?.value?.data?.booking_settings?.synxis_hotel_num
      }

      // Set Brand Landing Page Data
      else if (page.value.path === `/${currentResort.value.data.url_partial}`) {
        data.page_type = 'hotel brand page'
        data.hotel_brand = (page.value as { layout: string })?.layout
      }

      // console.log('[debug] utag data:', JSON.parse(JSON.stringify(data)))
      // console.log('[debug] page.path:', page.value.path)
      // console.log('[debug] currentResort.url_partial:', currentResort.value.data.url_partial)
      // console.log('[debug] isHotelPropertyPage:', isHotelPropertyPage())

      if (typeof window.utag === 'object' && window.utag !== null) {
        (window.utag as { view: (data: Record<string, any>) => void }).view(data)
      }
    }).catch((error) => {
      console.error(error)
    })
  }

  await tealiumView()
}
